import React, {ReactNode} from "react";
import AdminContext from "./AdminContext";
import AbstractPage from "../shared/AbstractPage";
import MessageLog from "../shared/log/MessageLog";
import i18next from "i18next";
import LoadingOverlay from "../shared/session/components/LoadingOverlay";
import portalConfiguration from "../shared/configuration/PortalConfiguration";
import AdminMenuBar from "./menu/AdminMenuBar";
import AdminDialogs, {AdminDialogsGlobals} from "./dialog/AdminDialogs";
import AdminScreen from "./adminscreen/AdminScreen";
import "./index.scss";
import ContentMode from "./ContentMode";
import Administration, {AdministrationGlobals} from "./session/Administration";
import {
	ApplicationConfigKeystore,
	ApplicationInterface,
	BackgroundFileDataStore,
	FileGroupDataStore,
	Information,
	License,
	LogoFileDataStore,
	NewsPanel,
	OAuth,
	Portal,
	ProxiesApplication,
	ServerConfigLocalAuth,
	ServerConfigUserStorage,
	ServerInterface,
	ServerStatus,
	ThemeFileDataStore,
	TruststoreServer,
	UserCredentials,
	UserInterfaceResources,
	Users
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {MessageType} from "../shared/log/MessageInterface";
import MenuButton from "../shared/menu/MenuButton";
import {RestartDialogDefinition} from "./dialog/components/RestartDialog";
import MessageBox from "../shared/components/MessageBox";
import Translate from "../shared/translation/Translate";
import HelpOverlay, {HelpOverlayGlobals} from "../shared/components/HelpOverlay";
import FunctionHelper from "../shared/helper/FunctionHelper";
import {Routes} from "./Routes";
import {ProxyDialogDefinition} from "./dialog/components/ProxyDialog";
import {OAuthServerDialogDefinition} from "./dialog/components/OAuthServerDialog";
import {LicenseDialogDefinition} from "./dialog/components/LicenseDialog";
import {KeystoreDialogDefinition} from "./dialog/components/KeystoreDialog";
import {TruststoreDialogDefinition} from "./dialog/components/TruststoreDialog";
import {TsaDialogDefinition} from "./dialog/components/TsaDialog";
import {LocalAuthDialogDefinition} from "./dialog/components/LocalAuthDialog";
import {NewsDialogDefinition} from "./dialog/components/NewsDialog";
import {UserDialogDefinition} from "./dialog/components/UserDialog";
import {UserSourceDialogDefinition} from "./dialog/components/UserSourceDialog";
import {OAuthPortalDialogDefinition} from "./dialog/components/OAuthPortalDialog";
import {FunctionsDialogDefinition} from "./dialog/components/FunctionsDialog";
import {DesignDialogDefinition} from "./dialog/components/DesignDialog";
import {BackgroundType, DesignType, LogoType, ThemeType} from "../shared/models/DesignType";
import emptyImage from "../../resources/images/empty.png";
import {PortalAuthDialogDefinition} from "./dialog/components/PortalAuthDialog";
import ReconnectDialog from "../shared/dialog/components/ReconnectDialog";
import {AbstractPageState} from "../shared/AbstractPageInterface";
import {isDesktop, isMobile} from 'react-device-detect';
import {AdminDebugGlobals, initAdminDebugGlobals} from "./dialog/components/AdminDebugDialog";
import {Tooltip} from "primereact/tooltip";
import {PortalTsa} from "../shared/models/PortalTsa";

export interface AdminGlobals extends AdministrationGlobals {
	context: AdminContext;
	contentMode: ContentMode;
}

export function initAdminGlobals(): AdminGlobals {
	return {
		context: new AdminContext(),
		contentMode: ContentMode.INFO
	};
}

export interface AdminState extends AdminGlobals, AbstractPageState, AdminDialogsGlobals, HelpOverlayGlobals,
	AdminDebugGlobals {
	showRestartMessage: boolean;
	loading: boolean;
}

export default class Admin extends AbstractPage<any, AdminState> {
	public static route: string = "admin";

	/**
	 * Page component constructor
	 */
	constructor(props: Readonly<any>) {
		super(props);

		this.state = {
			...this.state,
			...initAdminGlobals(),
			...initAdminDebugGlobals(),
			loading: true,
			showRestartMessage: false
		};

		this.handleClick = this.handleClick.bind(this);
		this.loadDesignParameter = this.loadDesignParameter.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleWheel = this.handleWheel.bind(this);
	}

	private async loadDesignParameter(): Promise<DesignType> {
		let administration: Administration = this.state.context.session.getAdministration();

		let logoDataStore: LogoFileDataStore = await administration.getDatastore(
			FileGroupDataStore.Logo
		) as LogoFileDataStore;

		let logoType: LogoType = LogoType.DEFAULT;
		if (FunctionHelper.exists(logoDataStore.fileContent) && logoDataStore.fileContent !== "") {
			logoType = LogoType.CUSTOM;
		}

		let backgroundDataStore: BackgroundFileDataStore =
			await administration.getDatastore(FileGroupDataStore.Background) as BackgroundFileDataStore;

		let backgroundType: BackgroundType = BackgroundType.DEFAULT;
		if (
			FunctionHelper.exists(backgroundDataStore.fileContent) &&
			backgroundDataStore.fileContent === emptyImage.substring(emptyImage.indexOf(',') + 1)
		) {
			backgroundType = BackgroundType.HIDDEN;
		} else if (FunctionHelper.exists(backgroundDataStore.fileContent) && backgroundDataStore.fileContent !== "") {
			backgroundType = BackgroundType.CUSTOM;
		}

		let themeDataStore: ThemeFileDataStore =
			await administration.getDatastore(FileGroupDataStore.Theme) as ThemeFileDataStore;

		let themeType: ThemeType = ThemeType.DEFAULT;
		if (FunctionHelper.exists(themeDataStore.fileContent) && themeDataStore.fileContent !== "") {
			themeType = ThemeType.CUSTOM;

			for (let theme of Object.values(ThemeType)) {
				if (themeDataStore.fileContent!.indexOf("webpdf:theme " + theme) > -1) {
					themeType = theme as ThemeType;
					break;
				}
			}
		}

		return DesignType.fromJson({
			logoType: logoType,
			logo: logoDataStore.fileContent,
			themeType: themeType,
			theme: themeDataStore.fileContent,
			backgroundType: backgroundType,
			background: backgroundDataStore.fileContent,
			hideBackground: backgroundType === BackgroundType.HIDDEN
		} as DesignType);
	}

	private async handleRoutes(): Promise<void> {
		let user: UserCredentials | undefined = this.state.context.user;

		if (!FunctionHelper.applyValue(user?.isAdmin, UserCredentials.getIsAdminDefault())) {
			return;
		}

		let portalInfo: Information = this.state.context.info;
		let serverConfig: ServerInterface | undefined = this.state.serverConfig?.get();
		let applicationConfig: ApplicationInterface | undefined = this.state.applicationConfig?.get();
		let updates: any = {};

		switch (window.location.search) {
			case Routes.WEBSERVICES:
				updates.contentMode = ContentMode.WEBSERVICES;
				break;
			case Routes.STATISTICS:
				updates.contentMode = ContentMode.STATISTICS
				break;
			case Routes.LOGS:
				updates.contentMode = ContentMode.LOGS
				break;
			case Routes.SUPPORT:
				updates.contentMode = ContentMode.SUPPORT
				break;
			case Routes.DOCUMENTATION:
				updates.contentMode = ContentMode.DOCUMENTATION
				break;
			case Routes.CONNECTIONS:
				updates.contentMode = ContentMode.CONNECTION
				break;
			case Routes.SESSIONS:
				updates.contentMode = ContentMode.SESSIONS
				break;
			case Routes.SERVER_RESTART:
				if (RestartDialogDefinition.isActive(portalInfo, user)) {
					updates.showRestartDialog = true;
				}
				break;
			case Routes.SERVER_PROXY:
				if (ProxyDialogDefinition.isActive(portalInfo, user)) {
					updates.showProxyDialog = true;
					updates.proxyParameter = ProxiesApplication.fromJson(
						FunctionHelper.applyValue(applicationConfig?.proxies, {})
					);
				}
				break;
			case Routes.SERVER_OAUTH:
				if (OAuthServerDialogDefinition.isActive(portalInfo, user)) {
					updates.showOAuthServerDialog = true;
					updates.oAuthServerParameter = OAuth.fromJson(
						FunctionHelper.applyValue(serverConfig?.oauth, {})
					);
				}
				break;
			case Routes.SERVER_LICENSE:
				if (LicenseDialogDefinition.isActive(portalInfo, user)) {
					updates.showLicenseDialog = true;
					updates.licenseParameter = License.fromJson(
						FunctionHelper.applyValue(applicationConfig?.license, {})
					);
				}
				break;
			case Routes.SERVER_KEYSTORE:
				if (KeystoreDialogDefinition.isActive(portalInfo, user)) {
					updates.showKeystoreDialog = true;
					updates.keystoreParameter = ApplicationConfigKeystore.fromJson(
						FunctionHelper.applyValue(applicationConfig?.keystore, {})
					);
				}
				break;
			case Routes.SERVER_TRUSTSTORE:
				if (TruststoreDialogDefinition.isActive(portalInfo, user)) {
					updates.showTruststoreDialog = true;
					updates.truststoreParameter = TruststoreServer.fromJson(
						FunctionHelper.applyValue(serverConfig?.truststore, {})
					);
				}
				break;
			case Routes.SERVER_TSA:
				if (TsaDialogDefinition.isActive(portalInfo, user)) {
					updates.showTsaDialog = true;
					updates.tsaParameter = PortalTsa.fromJson(
						FunctionHelper.applyValue(applicationConfig?.tsa, {})
					);
				}
				break;
			case Routes.SERVER_AUTH:
				if (LocalAuthDialogDefinition.isActive(portalInfo, user)) {
					updates.showLocalAuthDialog = true;
					updates.LocalAuthParameter = ServerConfigLocalAuth.fromJson(
						FunctionHelper.applyValue(serverConfig?.localAuth, {})
					);
				}
				break;
			case Routes.PORTAL_NEWS:
				if (NewsDialogDefinition.isActive(portalInfo, user)) {
					updates.showNewsDialog = true;
					updates.newsParameter = NewsPanel.fromJson(
						FunctionHelper.applyValue(applicationConfig?.portal.newsPanel, {})
					);
				}
				break;
			case Routes.PORTAL_USERS:
				if (UserDialogDefinition.isActive(portalInfo, user)) {
					updates.showUserDialog = true;
					updates.userParameter = Users.fromJson(
						FunctionHelper.applyValue(this.state.userConfig?.get(), {})
					);
				}
				break;
			case Routes.PORTAL_USERSOURCE:
				if (UserSourceDialogDefinition.isActive(portalInfo, user)) {
					updates.showUserSourceDialog = true;
					updates.userSourceParameter = ServerConfigUserStorage.fromJson(
						FunctionHelper.applyValue(serverConfig?.user, {})
					);
				}
				break;
			case Routes.PORTAL_OAUTH:
				if (OAuthPortalDialogDefinition.isActive(portalInfo, user)) {
					updates.showOAuthPortalDialog = true;
					updates.oAuthPortalParameter = OAuth.fromJson(
						FunctionHelper.applyValue(serverConfig?.oauth, {})
					);
				}
				break;
			case Routes.PORTAL_FUNCTIONS:
				if (FunctionsDialogDefinition.isActive(portalInfo, user)) {
					updates.showFunctionsDialog = true;
					updates.functionsParameter = Portal.fromJson(
						FunctionHelper.applyValue(applicationConfig?.portal, {})
					);
				}
				break;
			case Routes.PORTAL_DESIGN:
				if (DesignDialogDefinition.isActive(portalInfo, user)) {
					updates.showDesignDialog = true;
					updates.designParameter = await this.loadDesignParameter();
				}
				break;
			case Routes.PORTAL_AUTH:
				if (PortalAuthDialogDefinition.isActive(portalInfo, user)) {
					updates.showPortalAuthDialog = true;
					updates.portalAuthParameter = Portal.fromJson(
						FunctionHelper.applyValue(applicationConfig?.portal, {})
					);
				}
				break;
		}

		this.setState({...updates});
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		let context: AdminContext = this.state.context;
		await context.initialize(this);

		if (FunctionHelper.exists(this.state.context.user) &&
			!FunctionHelper.applyValue(this.state.context.user?.isAdmin, UserCredentials.getIsAdminDefault())) {
			this.setState({
				showAdminLogoutDialog: true
			});
		}

		await this.handleRoutes();

		window.addEventListener('keydown', this.handleKeyDown);
		window.addEventListener('wheel', this.handleWheel, {passive: false});

		if (isMobile) {
			window.document.body.classList.add("mobile-device");
		} else if (isDesktop) {
			window.document.body.classList.add("desktop-device");
		}

		this.setState({
			loading: false
		});
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('keydown', this.handleKeyDown);
		window.removeEventListener('wheel', this.handleWheel);
	}

	private handleKeyDown(event: KeyboardEvent) {
		// Prevent browser zoom
		if (event.ctrlKey && (event.key === "+" || event.key === "-")) {
			event.preventDefault();
		}
	}

	private handleWheel(event: WheelEvent) {
		// Prevent browser zoom
		if (event.ctrlKey) {
			event.preventDefault();
		}
	}

	private handleClick(): void {
		this.state.context.session.resetIdle();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!this.state.context.initialized || this.state.loading) {
			return (
				<>
					{FunctionHelper.applyValue(this.state.reconnectDialogTries, 0) >= 1 ?
						<ReconnectDialog {...this.state} visible={true} language={i18next.language}
										 tries={this.state.reconnectDialogTries}
										 hide={() => this.props.context.getPage().setState({
											 showReconnectDialog: false
										 })}/> :
						<LoadingOverlay visible={true} language={i18next.language}/>
					}
					<div className="page" ref={this.pageElement}>
						<div className="message-panel">{this.renderMessages()}</div>
					</div>
				</>
			)
		}

		let user: UserCredentials | undefined = this.state.context.user;
		let portalInfo: Information = this.state.context.info;
		let serverStatus: ServerStatus = ServerStatus.fromJson(
			this.state.serverStatus?.get()
		);

		let logo: string = FunctionHelper.applyValue(
			portalInfo.userInterface?.resources?.logoFileName, UserInterfaceResources.getLogoFileNameDefault()
		);
		if (FunctionHelper.isEmpty(logo)) {
			logo = "logo" + portalConfiguration.cachingPath + ".png";
		}

		let theme: string = FunctionHelper.applyValue(
			portalInfo.userInterface?.resources?.themeFileName, UserInterfaceResources.getThemeFileNameDefault()
		);
		if (FunctionHelper.isEmpty(theme)) {
			theme = "theme" + portalConfiguration.cachingPath + ".css";
		}

		let background: string = FunctionHelper.applyValue(
			portalInfo.userInterface?.resources?.backgroundFileName, UserInterfaceResources.getBackgroundFileNameDefault()
		);
		if (FunctionHelper.isEmpty(background)) {
			background = "background" + portalConfiguration.cachingPath + ".png";
		}

		let serverVersion: string = FunctionHelper.applyValue(
			portalInfo.serverVersion, Information.getServerVersionDefault()
		).replace(/ *\([^)]*\) */g, "").trim();
		let serverVersionParts: Array<string> = serverVersion.split(" ");

		return (
			<>
				<div className="admin-label">admin</div>
				<div className="page admin" ref={this.pageElement} hidden={!this.state.context.initialized}
					 onDrop={(event) => event.preventDefault()}
					 onDragOver={(event) => event.preventDefault()}
					 onClick={() => this.handleClick()}>
					<div className="header">
						<div className="header-menu">
							<div className="logo">
								<img src={portalConfiguration.baseUrl + "image/res/" + logo} alt="webPDF Logo"
									 onClick={() => window.location.reload()}/>
								<p className="version">
									<span>{serverVersionParts[0]} {serverVersionParts[1]}</span>
									{serverVersionParts.length > 2 ? <><br/><span>{serverVersionParts[2]}</span></> : <></>}
								</p>
							</div>
							<AdminMenuBar {...this.state} language={i18next.language} userId={user?.uniqueId}/>
						</div>
					</div>
					<div className="content">
						{this.state.showRestartMessage &&
							<MessageBox {...this.props} language={i18next.language} className="restart-message"
										closeable={true} type={MessageType.INFO}>
								<Translate language={i18next.language} data="adminscreen.restart" namespace="screen"/>
								<MenuButton {...this.props} language={i18next.language} style={{marginLeft: "10px"}}
											disabled={!RestartDialogDefinition.isActive(portalInfo, user)}
											label={Translate.value("server.restart", {ns: "menu"})}
											onClick={() => this.setState({
												showRestartDialog: true
											})}/>
							</MessageBox>
						}
						<AdminScreen {...this.state} language={i18next.language}/>
						<AdminDialogs {...this.state} language={i18next.language}/>
					</div>
					<div className="message-panel">{this.renderMessages()}</div>
					<MessageLog {...this.state} resizeContainer={this.pageElement} language={i18next.language}/>
					<HelpOverlay {...this.state} language={i18next.language}/>
				</div>
				<img className="background-image" src={portalConfiguration.baseUrl + "image/res/" + background}
					 alt="webPDF Background"/>
				<link href={portalConfiguration.baseUrl + "css/res/" + theme} rel="stylesheet" type="text/css"/>
				<Tooltip target=".tooltip" style={{maxWidth: "300px", whiteSpace: "normal"}}
						 appendTo={document.getElementById("root")}/>
			</>
		);
	}
}